// hooks/useSpreadsheetExtractor.js
import { useCallback } from "react";
import {
  extractTableByMetadata,
  extractTableByPosition,
  findCellsByText,
  findTableDataLength,
  findValueByLabel,
} from "../../../../utils/spreadsheet/spreadsheetExtractor";

export const useSpreadsheetExtractor = () => {
  const extractData = useCallback((univerAPI, config, options = {}) => {
    const { formData = {}, transform = null } = options;

    const fWorkbook = univerAPI.getActiveWorkbook();
    if (!fWorkbook) {
      throw new Error("Workbook não encontrado");
    }

    const fWorksheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
    console.log("fWorksheet ok?", typeof fWorksheet?.getMaxRows === "function");
    if (!fWorksheet) {
      throw new Error("Worksheet não encontrada");
    }

    console.log("🔍 Extraindo dados da planilha com config:", config);

    const result = {
      tables: {},
      totals: {},
      metadata: {
        extractedAt: new Date().toISOString(),
      },
    };

    // === 1. Extrai tabelas ===
    if (config.tables && config.tables.length > 0) {
      config.tables.forEach((tableConfig) => {
        const extracted = extractTable(fWorksheet, tableConfig);
        result.tables[tableConfig.name] = extracted;
        result.metadata[`${tableConfig.name}_count`] = extracted.length;
        console.log(`✅ Tabela "${tableConfig.name}" extraída:`, extracted);
      });
    }

    // === 2. Extrai totais ===
    if (config.totals && config.totals.length > 0) {
      config.totals.forEach((totalConfig) => {
        const value = findValueByLabel(
          fWorksheet,
          totalConfig.label,
          totalConfig.offsetCol || 1,
        );
        if (value !== null) {
          result.totals[totalConfig.key] = value;
          console.log(`💰 Total "${totalConfig.key}" encontrado:`, value);
        } else {
          console.log(`⚠️ Total "${totalConfig.label}" não encontrado`);
        }
      });
    }

    // === 3. Complementa com dados do formulário ===
    if (formData && Object.keys(formData).length > 0) {
      Object.keys(result.tables).forEach((tableName) => {
        const tableData = result.tables[tableName];
        const formTableData = formData[tableName];
        if (Array.isArray(tableData) && Array.isArray(formTableData)) {
          result.tables[tableName] = tableData.map((item, index) => ({
            ...(formTableData[index] || {}),
            ...item,
          }));
        }
      });
    }

    // === 4. Aplica transformação customizada ===
    if (transform && typeof transform === "function") {
      return transform(result);
    }

    console.log("✅ Extração concluída:", result);
    return result; // <-- RETORNA OS DADOS EXTRAÍDOS, NÃO A CONFIG!
  }, []);

  return { extractData };
};

const extractTable = (fWorksheet, tableConfig) => {
  const {
    name,
    searchTitle,
    columns,
    useMetadata = false,
    tableDef = null,
    maxRows = 100, // <-- valor padrão mais seguro
    fallbackColumns = null,
  } = tableConfig;

  // Busca a tabela pelo título
  const titleCells = findCellsByText(fWorksheet, searchTitle, {
    maxRows: 200, // <-- busca limitada
    maxCols: 30,
  });

  if (titleCells.length === 0) {
    console.warn(`⚠️ Tabela '${searchTitle}' não encontrada`);
    return [];
  }

  const { row: titleRow, col: titleCol } = titleCells[0];
  const startRow = titleRow + 2;
  const startCol = titleCol;

  // Pega o número real de linhas/colunas da sheet
  const sheetRowCount = fWorksheet.getMaxRows();

  // Calcula quantas linhas podemos extrair com segurança
  const availableRows = sheetRowCount - startRow;
  const safeMaxRows = Math.min(maxRows, availableRows);

  let items = [];

  const columnsToUse = fallbackColumns || columns;
  const numColumnsToScan =
    tableDef?.columns?.length || columnsToUse?.length || 6;

  const realDataLength = findTableDataLength(
    fWorksheet,
    tableDef,
    startRow,
    startCol,
    numColumnsToScan,
    safeMaxRows,
  );

  if (useMetadata && tableDef) {
    items = extractTableByMetadata(
      fWorksheet,
      tableDef,
      columnsToUse,
      startRow,
      startCol,
      realDataLength,
    );

    if (items.length > 0) {
      console.log(`✅ ${name}: extraído por metadados (${items.length} itens)`);
      return formatItems(items, columns);
    }
  }

  if (columnsToUse && columnsToUse.length > 0) {
    items = extractTableByPosition(
      fWorksheet,
      columnsToUse,
      startRow,
      startCol,
      realDataLength, // era safeMaxRows
    );

    console.log(`🔄 ${name}: extraído por posição (${items.length} itens)`);
    return formatItems(items, columns);
  }

  return [];
};

const formatItems = (items, columns) => {
  if (!columns || columns.length === 0) return items;

  return items.map((item) => {
    const formatted = {};
    columns.forEach((col) => {
      if (typeof col === "string") {
        formatted[col] = item[col] ?? null;
      } else if (typeof col === "object" && col.key) {
        const value = item[col.key];
        formatted[col.key] = col.transform ? col.transform(value) : value;
      }
    });
    return formatted;
  });
};
